/**
 * Event compression for history timeline.
 * Transparently filters and merges events to show only semantically meaningful steps.
 */

import { EventLogEntry, EventType } from '~/server/src/dtos';

/**
 * Events that are never shown in the timeline (silently absorbed).
 */
export const HIDDEN_EVENTS: Set<EventType> = new Set([
  EventType.TM_GAMECREATED,
  EventType.STARTGAME, // Always followed by STARTROUND; redundant
  EventType.MERGECARDS,
  EventType.MERGECARDSOPP,
  EventType.STARTTURN,
  EventType.ENDTURN,
  EventType.NEWMESSAGE,
  EventType.SENDMESSAGE,
  EventType.GETGAMES,
  EventType.NEWGAME,
  EventType.JOINGAME,
  EventType.JOINGAMEOPP, // Not meaningful for game history review
  EventType.RESTOREGAME,
  EventType.GETTOURNAMENTS,
  EventType.JOINTOURNAMENT,
  EventType.LEAVEGAME,
  EventType.LEAVEGAMEOPP, // Not meaningful for game history review
  EventType.REJOINGAME, // Not meaningful for game history review
  EventType.TM_REJOINGAME, // Not meaningful for game history review
  EventType.UNKNOWN,
]);

/**
 * Events that become semantic steps (visible to the user).
 */
export const VISIBLE_EVENTS: Set<EventType> = new Set([
  EventType.STARTROUND,
  EventType.ENDROUND,
  EventType.ENDGAME,
  EventType.ADDBONUSCARDS,
  EventType.ADDBONUSCARDSOPP,
  EventType.SKIPPLAYER, // Player skipped due to no valid moves (informational, like ADDBONUSCARDSOPP)
  EventType.SELECTCARDDICE, // Show card selection before move
  EventType.SELECTDICES, // Show player dice roll before move selection
  EventType.SELECTDICESOPP, // Show opponent dice roll events before move selection
  EventType.SELECTMOVEOPP,
]);

/** Card value: number cards 3,4,6..12; joker -12..-9; bonus 0. Used for action icon display. */
export type CardValueForDisplay = number;

/**
 * A semantic step represents a meaningful event in the game history.
 * Each step maps to a range of raw events and has a target index for navigation.
 */
export type SemanticStep = {
  /** The visible event (for label/icon/timestamp) */
  entry: EventLogEntry;
  /** Index in the raw eventLog */
  originalIndex: number;
  /** eventIndex to set when clicking (= next step's originalIndex, or eventLog.length for last step) */
  targetEventIndex: number;
  /** For SELECTMOVEOPP: whether the move was triggered by card or dice (from preceding hidden event). */
  actionType?: 'card' | 'dice' | null;
  /** For card: card value (0, 3-12, -12..-9). For dice: 1-6. */
  actionValue?: number | null;
  /** Index in eventLog of the card/dice event that preceded this move (for reference). */
  actionEventIndex?: number | null;
  /** For SELECTDICES or SELECTDICESOPP: array of available dice values (1-6). */
  diceValues?: number[] | null;
  /** For SELECTDICESOPP: index into diceValues of the selected die (0-based), or null if unknown. */
  diceSelectedIndex?: number | null;
  /** True when this step is a merged roll+choose (two consecutive SELECTDICESOPP with same dice). */
  isMergedDiceRollAndChose?: boolean;
  /** For merged roll+choose: array of rolled dice values (from SELECTDICES), separate from chosen dice (diceValues). */
  rolledDiceValues?: number[] | null;
  /** For SELECTMOVEOPP (card): true when the played card exceeded the horse's handicap (server payload). */
  handicapExceeded?: boolean;
};

/** Valid card values for display: bonus 0, number 3,4,6..12, joker -12..-9 */
const VALID_CARD_VALUES = new Set([0, 3, 4, 6, 7, 8, 9, 10, 11, 12, -12, -11, -10, -9]);

/**
 * Extracts card value from a SELECTCARDDICE event payload.
 * Handles payload.card, payload.cardValue, payload.value.
 * Returns null if not found or invalid.
 */
export function extractCardValueFromPayload(entry: EventLogEntry): number | null {
  if (entry.eventType !== EventType.SELECTCARDDICE) return null;
  const p = entry.payload as Record<string, unknown> | undefined;
  if (!p) return null;
  const raw =
    (p.card as number | undefined) ??
    (p.cardValue as number | undefined) ??
    (p.value as number | undefined);
  if (typeof raw !== 'number') return null;
  return VALID_CARD_VALUES.has(raw) ? raw : null;
}

/**
 * Extracts dice value (1-6) from SELECTDICES or SELECTDICESOPP event payload.
 * For multiple dice, uses first die or turndicescur.
 * Returns null if not found or invalid.
 */
export function extractDiceValueFromPayload(entry: EventLogEntry): number | null {
  if (entry.eventType !== EventType.SELECTDICES && entry.eventType !== EventType.SELECTDICESOPP)
    return null;
  const p = entry.payload as Record<string, unknown> | undefined;
  if (!p) return null;
  let raw: number | undefined;
  if (entry.eventType === EventType.SELECTDICESOPP) {
    raw = (p.turndicescur as number) ?? (p.dice as number) ?? (p.value as number);
  } else {
    const dices = p.turndices ?? p.dices ?? p.dice;
    if (Array.isArray(dices) && dices.length > 0 && typeof dices[0] === 'number') {
      raw = dices[0];
    } else {
      raw = (p.dice as number) ?? (p.value as number);
    }
  }
  if (typeof raw !== 'number' || raw < 1 || raw > 6) return null;
  return raw;
}

/** Coerces a value to a valid die value (1-6) or returns undefined. */
function toDieValue(v: unknown): number | undefined {
  if (typeof v === 'number' && v >= 1 && v <= 6) return v;
  if (typeof v === 'string') {
    const n = parseInt(v, 10);
    if (Number.isInteger(n) && n >= 1 && n <= 6) return n;
  }
  return undefined;
}

/**
 * Extracts dice values from any payload that may contain turndices, dices, or diceValues.
 * Used for SELECTDICES/SELECTDICESOPP and optionally SELECTMOVEOPP when server sends dice on the move.
 */
function extractDiceValuesFromPayloadLike(payload: unknown): number[] | null {
  const p = payload as Record<string, unknown> | undefined;
  if (!p) return null;
  const arr =
    (p.turndices as number[] | undefined) ??
    (p.dices as number[] | undefined) ??
    (p.diceValues as number[] | undefined);
  if (Array.isArray(arr) && arr.length > 0) {
    const values = arr.map((v) => toDieValue(v)).filter((v): v is number => v !== undefined);
    if (values.length > 0) return values;
  }
  const single = toDieValue(p.dice ?? p.value);
  if (single !== undefined) return [single];
  return null;
}

/**
 * Extracts all dice values from SELECTDICES or SELECTDICESOPP event payload.
 * Returns array of valid dice values (1-6) or null if not found or invalid.
 * Tries turndices, dices, diceValues; supports number[] or string/coercible elements.
 */
export function extractDiceValuesFromPayload(entry: EventLogEntry): number[] | null {
  if (entry.eventType !== EventType.SELECTDICES && entry.eventType !== EventType.SELECTDICESOPP)
    return null;
  return extractDiceValuesFromPayloadLike(entry.payload);
}

/**
 * Tries to get dice values from a SELECTMOVEOPP payload (e.g. when server sends turndices on the move).
 * Returns null if not present or not SELECTMOVEOPP.
 */
export function extractDiceValuesFromMovePayload(entry: EventLogEntry): number[] | null {
  if (entry.eventType !== EventType.SELECTMOVEOPP) return null;
  return extractDiceValuesFromPayloadLike(entry.payload);
}

/**
 * Extracts the selected dice index (0-based) from SELECTDICESOPP event payload.
 * turndicescur is the index into turndices of the die the player picked.
 * Supports both 0-based (0 .. length-1) and 1-based (1 .. length) server values.
 * Returns null if not found or out of range.
 */
export function extractDiceSelectedIndexFromPayload(entry: EventLogEntry): number | null {
  if (entry.eventType !== EventType.SELECTDICESOPP) return null;
  const p = entry.payload as Record<string, unknown> | undefined;
  if (!p) return null;
  const turndices = p.turndices as number[] | undefined;
  const cur = p.turndicescur as number | undefined;
  if (typeof cur !== 'number' || !Array.isArray(turndices) || turndices.length === 0) return null;
  if (cur >= 0 && cur < turndices.length) return cur;
  if (cur >= 1 && cur <= turndices.length) return cur - 1;
  return null;
}

/**
 * Filters dice values to only include selected dice based on the diceSelectedIndex.
 * If diceSelectedIndex is null or out of bounds, returns all dice values.
 * Used for SELECTMOVEOPP events to show only the dice actually used for the move.
 */
function filterDiceValuesBySelection(
  diceValues: number[] | null,
  diceSelectedIndex: number | null,
): number[] | null {
  if (!diceValues || diceValues.length === 0) return diceValues;
  if (diceSelectedIndex === null) return diceValues;
  if (diceSelectedIndex < 0 || diceSelectedIndex >= diceValues.length) return diceValues;
  // Return only the selected die
  return [diceValues[diceSelectedIndex]];
}

/**
 * Turn info at a given event index: turn number (1-based count of STARTTURN so far) and turntype (card/dice).
 * Used for history panel turn sticky headers.
 */
export function getTurnInfoAtEventIndex(
  eventLog: EventLogEntry[],
  index: number,
): { turnNumber: number; turntype: 'card' | 'dice' | null } {
  let turnNumber = 0;
  for (let i = 0; i <= index; i++) {
    const entry = eventLog[i];
    if (entry?.eventType === EventType.STARTTURN) turnNumber++;
  }
  const turntype = getTurntypeAtEventIndex(eventLog, index);
  return { turnNumber, turntype };
}

/**
 * Scans backwards from the given event index for the most recent STARTTURN event
 * and returns its turntype for fallback classification (card vs dice).
 * Returns 'dice' | 'card' | null. Maps server turntype (e.g. DICES, CARDS or numeric) to normalized form.
 */
function getTurntypeAtEventIndex(eventLog: EventLogEntry[], index: number): 'dice' | 'card' | null {
  for (let i = index - 1; i >= 0; i--) {
    const entry = eventLog[i];
    if (!entry || entry.eventType !== EventType.STARTTURN) continue;
    const p = entry.payload as Record<string, unknown> | undefined;
    const turntype = p?.turntype;
    if (turntype === undefined) return null;
    const s = String(turntype).toUpperCase();
    if (s === 'DICES' || s === 'DICE') return 'dice';
    if (s === 'CARDS' || s === 'CARD') return 'card';
    // Server tmmodel: CARDS = 0, DICES = 1
    if (typeof turntype === 'number') return turntype === 0 ? 'card' : 'dice';
    return null;
  }
  return null;
}

/**
 * Finds the preceding dice event (SELECTDICES or SELECTDICESOPP) before fromIndex and returns its dice values.
 * Stops at previous SELECTMOVEOPP so each move gets its own roll. Used to ensure dice move labels show values.
 */
export function getPrecedingDiceValues(
  eventLog: EventLogEntry[],
  fromIndex: number,
): number[] | null {
  for (let i = fromIndex - 1; i >= 0; i--) {
    const entry = eventLog[i];
    if (!entry) continue;
    if (entry.eventType === EventType.SELECTMOVEOPP) break;
    if (
      entry.eventType === EventType.STARTROUND ||
      entry.eventType === EventType.ENDROUND ||
      entry.eventType === EventType.ENDGAME
    ) {
      break;
    }
    if (entry.eventType === EventType.SELECTDICES || entry.eventType === EventType.SELECTDICESOPP) {
      const vals = extractDiceValuesFromPayload(entry);
      if (vals && vals.length > 0) return vals;
    }
  }
  return null;
}

/**
 * Finds the immediate preceding action event (SELECTCARDDICE or SELECTDICES/SELECTDICESOPP)
 * before a SELECTMOVEOPP event. Stops at the previous SELECTMOVEOPP to ensure each action
 * event is only used once, eliminating ambiguity for values 3, 4, 6.
 *
 * This uses the immutable event log as the source of truth - if SELECTCARDDICE came before,
 * it's a card move; if SELECTDICES/SELECTDICESOPP came before, it's a dice move.
 */
function findPrecedingActionEvent(
  eventLog: EventLogEntry[],
  fromIndex: number,
): { type: 'card' | 'dice'; value: number | null; index: number } | null {
  // Start from the event immediately before SELECTMOVEOPP
  for (let i = fromIndex - 1; i >= 0; i--) {
    const entry = eventLog[i];
    if (!entry) continue;

    // Stop at previous SELECTMOVEOPP - action events before it were already consumed
    if (entry.eventType === EventType.SELECTMOVEOPP) {
      break;
    }

    // Stop at boundary events (round boundaries)
    if (
      entry.eventType === EventType.STARTROUND ||
      entry.eventType === EventType.ENDROUND ||
      entry.eventType === EventType.ENDGAME
    ) {
      break;
    }

    // Return immediately when we find an action event (card takes precedence if both exist)
    if (entry.eventType === EventType.SELECTCARDDICE) {
      const value = extractCardValueFromPayload(entry);
      return { type: 'card', value, index: i };
    }
    if (entry.eventType === EventType.SELECTDICES || entry.eventType === EventType.SELECTDICESOPP) {
      const value = extractDiceValueFromPayload(entry);
      return { type: 'dice', value, index: i };
    }
  }
  return null;
}

/**
 * Builds semantic steps from the raw event log.
 * Applies compression rules to show only meaningful events.
 *
 * @param eventLog - The full event log
 * @returns Array of semantic steps
 */
export function buildSemanticSteps(eventLog: EventLogEntry[]): SemanticStep[] {
  // Step 1: Collect visible events with their original indices
  const visibleEvents: Array<{ entry: EventLogEntry; originalIndex: number }> = [];
  for (let i = 0; i < eventLog.length; i++) {
    const entry = eventLog[i];
    // Filter out chat messages (already handled elsewhere, but double-check)
    if (entry.eventType === EventType.NEWMESSAGE || entry.eventType === EventType.SENDMESSAGE) {
      continue;
    }
    // Include if it's a visible event
    if (VISIBLE_EVENTS.has(entry.eventType)) {
      visibleEvents.push({ entry, originalIndex: i });
    }
  }

  // Step 2: Apply merge rules
  type MergedEntry = {
    entry: EventLogEntry;
    originalIndex: number;
    mergedNextOriginalIndex?: number;
  };
  const mergedEvents: MergedEntry[] = [];
  for (let i = 0; i < visibleEvents.length; i++) {
    const current = visibleEvents[i];
    const next = visibleEvents[i + 1];

    // Merge rule: Last ENDROUND + ENDGAME
    // If current is ENDROUND and next is ENDGAME (with only hidden events between), drop ENDROUND
    if (
      current.entry.eventType === EventType.ENDROUND &&
      next?.entry.eventType === EventType.ENDGAME
    ) {
      const hasOnlyHiddenBetween = hasOnlyHiddenEventsBetween(
        eventLog,
        current.originalIndex,
        next.originalIndex,
      );
      if (hasOnlyHiddenBetween) {
        continue;
      }
    }

    // Merge rule: SELECTDICES (server roll) + SELECTDICESOPP (player selection) with same turndices → one step
    // No senderId check: SELECTDICES is "SERVER", SELECTDICESOPP is the player
    if (
      current.entry.eventType === EventType.SELECTDICES &&
      next?.entry.eventType === EventType.SELECTDICESOPP
    ) {
      const curDice = extractDiceValuesFromPayload(current.entry);
      const nextDice = extractDiceValuesFromPayload(next.entry);
      const sameDice =
        curDice &&
        nextDice &&
        curDice.length === nextDice.length &&
        [...curDice].sort().join(',') === [...nextDice].sort().join(',');
      if (
        sameDice &&
        hasOnlyHiddenEventsBetween(eventLog, current.originalIndex, next.originalIndex)
      ) {
        mergedEvents.push({
          entry: next.entry, // SELECTDICESOPP has turndicescur
          originalIndex: current.originalIndex,
          mergedNextOriginalIndex: next.originalIndex,
        });
        i++; // skip next
        continue;
      }
    }

    mergedEvents.push({ entry: current.entry, originalIndex: current.originalIndex });
  }

  // Step 3: Build semantic steps with target indices and action metadata for SELECTMOVEOPP
  const steps: SemanticStep[] = [];
  // Track dice values per turn in forward pass (fixes bug where 2nd+ moves don't get dice values)
  let currentTurnDiceValues: number[] | null = null;
  let currentTurnDiceEventIndex: number | null = null;
  for (let i = 0; i < mergedEvents.length; i++) {
    const current = mergedEvents[i];
    const next = mergedEvents[i + 1];

    // Reset at round boundaries
    if (
      current.entry.eventType === EventType.STARTROUND ||
      current.entry.eventType === EventType.ENDROUND
    ) {
      currentTurnDiceValues = null;
      currentTurnDiceEventIndex = null;
    }

    // Track dice values when we see a dice event
    if (
      current.entry.eventType === EventType.SELECTDICES ||
      current.entry.eventType === EventType.SELECTDICESOPP
    ) {
      const diceVals = extractDiceValuesFromPayload(current.entry);
      if (diceVals && diceVals.length > 0) {
        currentTurnDiceValues = diceVals;
        currentTurnDiceEventIndex = current.originalIndex;
      }
    }

    // targetEventIndex = merged next (if merged roll+choose), else originalIndex of next semantic step, or eventLog.length
    const targetEventIndex =
      current.mergedNextOriginalIndex ?? (next ? next.originalIndex : eventLog.length);

    const step: SemanticStep = {
      entry: current.entry,
      originalIndex: current.originalIndex,
      targetEventIndex,
    };
    if (current.mergedNextOriginalIndex != null) {
      step.isMergedDiceRollAndChose = true;
    }

    if (current.entry.eventType === EventType.SELECTMOVEOPP) {
      const payload = current.entry.payload as Record<string, unknown> | undefined;
      const carddice = payload?.carddice as number | undefined;
      step.handicapExceeded = payload?.handicapExceeded === true;

      // Check previous semantic step to determine card vs dice (much simpler and more reliable)
      const previousStep = i > 0 ? mergedEvents[i - 1] : null;
      if (previousStep) {
        if (previousStep.entry.eventType === EventType.SELECTCARDDICE) {
          // Previous step was card selection
          const cardValue = extractCardValueFromPayload(previousStep.entry);
          step.actionType = 'card';
          step.actionValue = cardValue ?? carddice ?? null;
          step.actionEventIndex = previousStep.originalIndex;
        } else if (
          previousStep.entry.eventType === EventType.SELECTDICES ||
          previousStep.entry.eventType === EventType.SELECTDICESOPP
        ) {
          // Previous step was dice roll: use move value (carddice) for label, e.g. 6 for 4+2
          const diceValue = extractDiceValueFromPayload(previousStep.entry);
          step.actionType = 'dice';
          step.actionValue = carddice ?? diceValue ?? null;
          step.actionEventIndex = previousStep.originalIndex;
          const allDiceValues = extractDiceValuesFromPayload(previousStep.entry);
          // If SELECTDICESOPP, filter to only selected dice; otherwise use all dice
          if (previousStep.entry.eventType === EventType.SELECTDICESOPP) {
            const diceSelectedIndex = extractDiceSelectedIndexFromPayload(previousStep.entry);
            step.diceValues = filterDiceValuesBySelection(allDiceValues, diceSelectedIndex);
          } else {
            step.diceValues = allDiceValues;
          }
        } else {
          // Previous step is not a card/dice selection - fallback to searching raw event log
          const action = findPrecedingActionEvent(eventLog, current.originalIndex);
          if (action) {
            step.actionType = action.type;
            step.actionValue = action.value ?? carddice ?? null;
            step.actionEventIndex = action.index;
            if (action.type === 'dice' && action.index >= 0 && action.index < eventLog.length) {
              const diceEvent = eventLog[action.index];
              const allDiceValues = extractDiceValuesFromPayload(diceEvent);
              // If SELECTDICESOPP, filter to only selected dice; otherwise use all dice
              if (diceEvent.eventType === EventType.SELECTDICESOPP) {
                const diceSelectedIndex = extractDiceSelectedIndexFromPayload(diceEvent);
                step.diceValues = filterDiceValuesBySelection(allDiceValues, diceSelectedIndex);
              } else {
                step.diceValues = allDiceValues;
              }
            }
          } else {
            const turntype = getTurntypeAtEventIndex(eventLog, current.originalIndex);
            step.actionType = turntype === 'card' ? 'card' : turntype === 'dice' ? 'dice' : 'dice';
            step.actionValue = carddice ?? null;
          }
        }
      } else {
        const action = findPrecedingActionEvent(eventLog, current.originalIndex);
        if (action) {
          step.actionType = action.type;
          step.actionValue = action.value ?? carddice ?? null;
          step.actionEventIndex = action.index;
          if (action.type === 'dice' && action.index >= 0 && action.index < eventLog.length) {
            const diceEvent = eventLog[action.index];
            const allDiceValues = extractDiceValuesFromPayload(diceEvent);
            // If SELECTDICESOPP, filter to only selected dice; otherwise use all dice
            if (diceEvent.eventType === EventType.SELECTDICESOPP) {
              const diceSelectedIndex = extractDiceSelectedIndexFromPayload(diceEvent);
              step.diceValues = filterDiceValuesBySelection(allDiceValues, diceSelectedIndex);
            } else {
              step.diceValues = allDiceValues;
            }
          }
        } else {
          const turntype = getTurntypeAtEventIndex(eventLog, current.originalIndex);
          step.actionType = turntype === 'card' ? 'card' : turntype === 'dice' ? 'dice' : 'dice';
          step.actionValue = carddice ?? null;
        }
      }

      // Ensure every dice move has diceValues from forward-pass tracking (fixes bug where 2nd+ moves don't get dice values)
      if (step.actionType === 'dice') {
        // Use tracked dice values from forward pass (most reliable)
        if (currentTurnDiceValues && currentTurnDiceValues.length > 0) {
          // Check if there's a SELECTDICESOPP event before this SELECTMOVEOPP with a selection
          // Scan backwards from SELECTMOVEOPP to find the most recent SELECTDICESOPP
          let diceSelectedIndex: number | null = null;
          // Check the event at currentTurnDiceEventIndex first
          if (
            currentTurnDiceEventIndex != null &&
            currentTurnDiceEventIndex >= 0 &&
            currentTurnDiceEventIndex < eventLog.length
          ) {
            const diceEvent = eventLog[currentTurnDiceEventIndex];
            if (diceEvent.eventType === EventType.SELECTDICESOPP) {
              diceSelectedIndex = extractDiceSelectedIndexFromPayload(diceEvent);
            }
          }
          // Also check for merged events: if currentTurnDiceEventIndex points to SELECTDICES,
          // check if there's a merged SELECTDICESOPP right after it
          if (diceSelectedIndex === null && currentTurnDiceEventIndex != null) {
            const diceEventIndex = currentTurnDiceEventIndex;
            // Check merged events to see if SELECTDICES was merged with SELECTDICESOPP
            const mergedEvent = mergedEvents.find(
              (me) => me.originalIndex === diceEventIndex && me.mergedNextOriginalIndex != null,
            );
            if (mergedEvent && mergedEvent.mergedNextOriginalIndex != null) {
              const selectDicesOppIndex = mergedEvent.mergedNextOriginalIndex;
              if (selectDicesOppIndex >= 0 && selectDicesOppIndex < eventLog.length) {
                const selectDicesOppEvent = eventLog[selectDicesOppIndex];
                if (selectDicesOppEvent.eventType === EventType.SELECTDICESOPP) {
                  diceSelectedIndex = extractDiceSelectedIndexFromPayload(selectDicesOppEvent);
                }
              }
            }
          }
          // If still no selection found, scan backwards from SELECTMOVEOPP to find SELECTDICESOPP
          if (diceSelectedIndex === null) {
            for (let j = current.originalIndex - 1; j >= 0; j--) {
              const event = eventLog[j];
              if (!event) break;
              if (event.eventType === EventType.SELECTMOVEOPP) break; // Stop at previous move
              if (
                event.eventType === EventType.STARTROUND ||
                event.eventType === EventType.ENDROUND ||
                event.eventType === EventType.ENDGAME
              ) {
                break; // Stop at round boundaries
              }
              if (event.eventType === EventType.SELECTDICESOPP) {
                diceSelectedIndex = extractDiceSelectedIndexFromPayload(event);
                break; // Found the most recent SELECTDICESOPP
              }
            }
          }
          const filteredDiceValues = filterDiceValuesBySelection(
            currentTurnDiceValues,
            diceSelectedIndex,
          );
          step.diceValues = filteredDiceValues;
          step.actionEventIndex = currentTurnDiceEventIndex ?? step.actionEventIndex ?? null;
        } else {
          // Fallback: try from actionEventIndex if available
          const fromDiceEvent =
            step.actionEventIndex != null &&
            step.actionEventIndex >= 0 &&
            step.actionEventIndex < eventLog.length
              ? eventLog[step.actionEventIndex]
              : null;
          if (fromDiceEvent) {
            const allDiceValues = extractDiceValuesFromPayload(fromDiceEvent);
            // If SELECTDICESOPP, filter to only selected dice; otherwise use all dice
            if (fromDiceEvent.eventType === EventType.SELECTDICESOPP) {
              const diceSelectedIndex = extractDiceSelectedIndexFromPayload(fromDiceEvent);
              step.diceValues = filterDiceValuesBySelection(allDiceValues, diceSelectedIndex);
            } else {
              step.diceValues = allDiceValues;
            }
          } else {
            // Last resort: scan backwards for the preceding dice event
            const action = findPrecedingActionEvent(eventLog, current.originalIndex);
            if (action?.type === 'dice' && action.index >= 0 && action.index < eventLog.length) {
              const diceEvent = eventLog[action.index];
              const allDiceValues = extractDiceValuesFromPayload(diceEvent);
              // If SELECTDICESOPP, filter to only selected dice; otherwise use all dice
              if (diceEvent.eventType === EventType.SELECTDICESOPP) {
                const diceSelectedIndex = extractDiceSelectedIndexFromPayload(diceEvent);
                step.diceValues = filterDiceValuesBySelection(allDiceValues, diceSelectedIndex);
              } else {
                step.diceValues = allDiceValues;
              }
            }
          }
        }
      }
    }

    if (
      current.entry.eventType === EventType.SELECTDICES ||
      current.entry.eventType === EventType.SELECTDICESOPP
    ) {
      // Extract available dice values and which die was selected (for cross-out in UI)
      step.diceValues = extractDiceValuesFromPayload(current.entry);
      // Only SELECTDICESOPP has turndicescur (selected index), SELECTDICES doesn't
      if (current.entry.eventType === EventType.SELECTDICESOPP) {
        step.diceSelectedIndex = extractDiceSelectedIndexFromPayload(current.entry);
      }
      // For merged roll+choose: store rolled dice values separately from chosen dice
      if (step.isMergedDiceRollAndChose && current.mergedNextOriginalIndex != null) {
        // The originalIndex points to SELECTDICES (roll), mergedNextOriginalIndex points to SELECTDICESOPP (choose)
        const rollEvent = eventLog[current.originalIndex];
        if (rollEvent) {
          step.rolledDiceValues = extractDiceValuesFromPayload(rollEvent);
        }
      }
    }

    steps.push(step);
  }

  return steps;
}

/**
 * Checks if there are only hidden events between two indices (exclusive).
 */
function hasOnlyHiddenEventsBetween(
  eventLog: EventLogEntry[],
  startIndex: number,
  endIndex: number,
): boolean {
  for (let i = startIndex + 1; i < endIndex; i++) {
    const entry = eventLog[i];
    // If any event is visible (not hidden), return false
    if (
      !HIDDEN_EVENTS.has(entry.eventType) &&
      entry.eventType !== EventType.NEWMESSAGE &&
      entry.eventType !== EventType.SENDMESSAGE
    ) {
      return false;
    }
  }
  return true;
}
